import { WS_URL } from '@/config';
import { getToken } from '@/utils/request';

class SocketService {
    constructor() {
        this.defaultOptions = {
            token: '',
            heartbeatInterval: 30000, // 30秒心跳间隔
            reconnectInterval: 5000, // 5秒重连间隔
            maxReconnectAttempts: 5, // 最大重连次数
            autoReconnect: true, // 自动重连
            debug: process.env.NODE_ENV !== 'production',
            requireAuth: true, // 需要认证
        };

        this.socket = null;
        this.url = '';
        this.options = {};
        this.reconnectAttempts = 0;
        this.messageQueue = [];
        this.eventListeners = {
            open: [],
            message: [],
            close: [],
            error: [],
            authSuccess: [],
            authFail: [],
        };
        this.isAuthenticated = false;
        this.heartbeatTimer = null;
        this.reconnectTimer = null;
    }

    connect(url, options = {}) {
        this.options = {
            ...this.defaultOptions,
            ...options,
        };
        this.url = url || WS_URL;
        this.isAuthenticated = false;

        this._log('正在连接 WebSocket:', this.url);
        this.disconnect();

        this.socket = new WebSocket(this.url);
        this.socket.onopen = (event) => this._handleOpen(event);
        this.socket.onmessage = (event) => this._handleMessage(event);
        this.socket.onclose = (event) => this._handleClose(event);
        this.socket.onerror = (error) => this._handleError(error);
    }

    disconnect(code = 1000, reason = 'Manual disconnect') {
        this._log('正在断开 WebSocket 连接');
        this._stopHeartbeat();
        this._stopReconnect();

        if (this.socket) {
            this.socket.close(code, reason);
            this.socket = null;
        }

        this.reconnectAttempts = 0;
        this.isAuthenticated = false;
        this.messageQueue = [];
    }

    updateToken(token) {
        this.options.token = token || '';
    }

    send(message, queueIfDisconnected = true) {
        const formattedMessage = typeof message === 'string'
            ? message
            : JSON.stringify(message);
        const parsedMessage = typeof message === 'string'
            ? this._safeParse(message)
            : message;

        if (this.isConnected && this.socket.readyState === WebSocket.OPEN) {
            const isPing = parsedMessage && parsedMessage.action === 'PING';
            if (!isPing && this.options.requireAuth && !this.isAuthenticated) {
                this.messageQueue.push(formattedMessage);
                return;
            }

            try {
                this.socket.send(formattedMessage);
                this._log('消息已发送:', formattedMessage);
            } catch (error) {
                this._log('发送消息失败:', error);
                this._emit('error', error);
            }
            return;
        }

        if (queueIfDisconnected) {
            this.messageQueue.push(formattedMessage);
            this._log('连接未就绪，消息已加入队列:', formattedMessage);
        }
    }

    sendAuth() {
        if (!this.options.token) {
            this._log('未提供 token，跳过认证');
            return;
        }

        if (!this.isConnected) {
            this._log('连接未建立，无法认证');
            return;
        }

        this.isAuthenticated = false;
        this.socket.send(JSON.stringify({
            action: 'AUTH',
            params: {
                token: this.options.token,
            },
        }));
        this._log('已发送 AUTH 认证');
    }

    on(event, callback) {
        if (!this.eventListeners[event]) {
            console.warn(`未知事件类型: ${event}`);
            return;
        }

        if (!callback._eventId) {
            callback._eventId = this._generateEventId();
        }

        const exists = this.eventListeners[event].some(
            (cb) => cb._eventId === callback._eventId
        );
        if (!exists) {
            this.eventListeners[event].push(callback);
        }
    }

    off(event, callback) {
        if (!this.eventListeners[event]) {
            return;
        }

        if (!callback) {
            this.eventListeners[event] = [];
            return;
        }

        this.eventListeners[event] = this.eventListeners[event].filter(
            (cb) => !(cb._eventId && callback._eventId && cb._eventId === callback._eventId)
        );
    }

    _handleOpen(event) {
        this._log('WebSocket 连接已建立');
        this.reconnectAttempts = 0;
        this.isAuthenticated = !this.options.requireAuth;

        if (this.options.requireAuth) {
            this.sendAuth();
        }

        this._startHeartbeat();

        if (!this.options.requireAuth) {
            this._processMessageQueue();
        }

        this._emit('open', event);
    }

    _handleMessage(event) {
        try {
            let data = event.data;
            try {
                data = JSON.parse(data);
            } catch (e) {
                // keep raw data
            }

            this._log('收到消息:', data);

            if (data && data.code === 'OK' && !data.action) {
                this.isAuthenticated = true;
                this._emit('authSuccess', data);
                this._processMessageQueue();
                return;
            }

            if (data && (data.code === 'E200' || data.message === '无效token')) {
                this.isAuthenticated = false;
                this._emit('authFail', data);
                return;
            }

            if (data && data.type === 'pong') {
                return;
            }

            this._emit('message', data);
        } catch (error) {
            this._log('消息处理错误:', error);
            this._emit('error', error);
        }
    }

    _handleClose(event) {
        this._log(`连接关闭，code=${event.code}`);
        this.isAuthenticated = false;
        this._stopHeartbeat();
        this._emit('close', event);

        if (
            this.options.autoReconnect
            && event.code !== 1000
            && this.reconnectAttempts < this.options.maxReconnectAttempts
        ) {
            this.reconnectAttempts += 1;
            const delay = Math.min(
                this.options.reconnectInterval * Math.pow(2, this.reconnectAttempts - 1),
                30000
            );

            this._log(`${delay}ms 后尝试第 ${this.reconnectAttempts} 次重连`);
            this.reconnectTimer = setTimeout(() => {
                this.connect(this.url, this.options);
            }, delay);
        }
    }

    _handleError(error) {
        this._log('WebSocket 错误:', error);
        this._emit('error', error);
    }

    _startHeartbeat() {
        this._stopHeartbeat();
        this.heartbeatTimer = setInterval(() => {
            if (this.socket && this.socket.readyState === WebSocket.OPEN) {
                this.send({
                    action: 'PING',
                    params: {},
                }, false);
            }
        }, this.options.heartbeatInterval);
    }

    _stopHeartbeat() {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
    }

    _stopReconnect() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }

    _processMessageQueue() {
        while (
            this.messageQueue.length > 0
            && this.socket
            && this.socket.readyState === WebSocket.OPEN
            && (!this.options.requireAuth || this.isAuthenticated)
        ) {
            const message = this.messageQueue.shift();
            try {
                this.socket.send(message);
            } catch (error) {
                this._log('发送队列消息失败:', error);
                this._emit('error', error);
                break;
            }
        }
    }

    _emit(event, ...args) {
        if (!this.eventListeners[event]) {
            return;
        }

        this.eventListeners[event].forEach((callback) => {
            try {
                callback.apply(this, args);
            } catch (error) {
                console.error(`事件处理器错误 (${event}):`, error);
            }
        });
    }

    _safeParse(message) {
        try {
            return JSON.parse(message);
        } catch (e) {
            return null;
        }
    }

    _generateEventId() {
        return `evt_${Date.now()}_${Math.floor(Math.random() * 10000)}`;
    }

    _log(...args) {
        if (this.options.debug) {
            console.log('[AgentSocket]', ...args);
        }
    }

    get isConnected() {
        return this.socket && this.socket.readyState === WebSocket.OPEN;
    }

    get readyState() {
        return this.socket ? this.socket.readyState : WebSocket.CLOSED;
    }
}

const socket = new SocketService();

export function connectAgentSocket(token) {
    const authToken = token || getToken();
    if (!authToken) {
        return;
    }

    if (socket.isConnected && socket.options.token === authToken && socket.isAuthenticated) {
        return;
    }

    socket.connect(WS_URL, {
        token: authToken,
        requireAuth: true,
        autoReconnect: true,
    });
}

export function disconnectAgentSocket() {
    socket.disconnect();
}

export function updateAgentSocketToken(token) {
    socket.updateToken(token);
    if (socket.isConnected) {
        socket.sendAuth();
    }
}

export default socket;
